import { createElement, forwardRef, useEffect, useState } from "react";
import {
  createLucideIcon,
  HelpCircle,
  Icon,
  type IconNode,
  type LucideIcon,
} from "lucide-react";
import { iconAssetBase, iconFiles } from "virtual:lucide-icon-assets";
// Aliases share content hashes, so they also share component identities and downloads.
const configuredIconComponentsByHash = new Map<string, LucideIcon>();
const iconRequestsByHash = new Map<string, Promise<LucideIcon>>();
const loadedIconComponentsByHash = new Map<string, LucideIcon>();
const allowedSvgTags = new Set([
  "path",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "rect",
]);
const allowedSvgAttributes = new Set([
  "key",
  "d",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "x",
  "y",
  "x1",
  "y1",
  "x2",
  "y2",
  "width",
  "height",
  "points",
]);
export function isIconNode(value: unknown): value is IconNode {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (entry) =>
        Array.isArray(entry) &&
        entry.length === 2 &&
        allowedSvgTags.has(entry[0]) &&
        entry[1] !== null &&
        typeof entry[1] === "object" &&
        !Array.isArray(entry[1]) &&
        Object.entries(entry[1]).every(
          ([attributeName, attributeValue]) =>
            (attributeName === "fill" && attributeValue === "currentColor") ||
            (allowedSvgAttributes.has(attributeName) &&
              (typeof attributeValue === "string" ||
                typeof attributeValue === "number")),
        ),
    )
  );
}
function loadIconComponent(assetHash: string): Promise<LucideIcon> {
  const cachedRequest = iconRequestsByHash.get(assetHash);
  if (cachedRequest) return cachedRequest;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  const request = fetch(`${iconAssetBase}${assetHash}.json`, {
    signal: controller.signal,
    credentials: "omit",
  })
    .then(async (response) => {
      if (!response.ok)
        throw new Error(`Icon request failed: ${response.status}`);
      const payload = await response.json();
      if (typeof payload?.name !== "string" || !isIconNode(payload.iconNode))
        throw new Error("Invalid icon geometry");
      const component = createLucideIcon(payload.name, payload.iconNode);
      loadedIconComponentsByHash.set(assetHash, component);
      return component;
    })
    .catch((error: unknown) => {
      // Allow a later mount to retry rather than caching a transient failure forever.
      iconRequestsByHash.delete(assetHash);
      throw error;
    })
    .finally(() => clearTimeout(timeout));
  iconRequestsByHash.set(assetHash, request);
  return request;
}

export const resolveLucideIcon = (iconName: string): LucideIcon | null => {
  const normalizedIconName = iconName
    .trim()
    .replace(/-/g, "")
    .toLowerCase()
    .replace(/^lucide/, "")
    .replace(/icon$/, "");
  const assetHash = Object.prototype.hasOwnProperty.call(
    iconFiles,
    normalizedIconName,
  )
    ? iconFiles[normalizedIconName]
    : undefined;
  if (!assetHash) return null;
  const cachedComponent = configuredIconComponentsByHash.get(assetHash);
  if (cachedComponent) return cachedComponent;
  const ConfiguredIcon = forwardRef<
    SVGSVGElement,
    React.ComponentProps<LucideIcon>
  >((props, ref) => {
    const [LoadedIcon, setLoadedIcon] = useState<LucideIcon | null>(
      () => loadedIconComponentsByHash.get(assetHash) ?? null,
    );
    useEffect(() => {
      // Another mounted icon may need this request; ignore late callbacks instead of aborting it.
      let isMounted = true;
      loadIconComponent(assetHash).then(
        (icon) => {
          if (isMounted) setLoadedIcon(() => icon);
        },
        () => {
          if (isMounted) setLoadedIcon(() => HelpCircle);
        },
      );
      return () => {
        isMounted = false;
      };
    }, []);
    // Reserve the final SVG dimensions without suspending the surrounding card.
    return LoadedIcon
      ? createElement(LoadedIcon, { ...props, ref })
      : createElement(Icon, {
          "aria-hidden": true,
          ...props,
          ref,
          iconNode: [],
        });
  });
  ConfiguredIcon.displayName = `ConfiguredIcon(${normalizedIconName})`;
  configuredIconComponentsByHash.set(assetHash, ConfiguredIcon);
  return ConfiguredIcon;
};

// Optional format icons use the nullable resolver; amenities retain the help fallback.
export const getLucideIcon = (iconName: string): LucideIcon =>
  resolveLucideIcon(iconName) ?? HelpCircle;
